import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';

import { FlexTimeActivity } from 'libs/domain';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { SentryService } from '@app/src/app/minimal/services/Sentry/Sentry.service';
import { PaginatorComponent } from '@app/src/app/shared/components/paginator';
import { QueryParamService } from '@app/src/app/shared/services/filtering/query-param.service';
import { MediaService } from '@app/src/app/shared/services/media';

import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { FlexTimePermissionsService } from '@shared/services/flex-time/flex-time-permissions';

import {
  FLEX_ACTIVITY_TEMPLATE_TABLE_COLUMNS,
  FlexActivityTemplateQueryParams,
  FtmActivityTemplatesMessages,
  flexActivityTemplateQuerySchema,
} from './ftm-activity-templates.constants';
import { FtmActivityTemplatesService } from './services';

@Component({
  templateUrl: './ftm-activity-templates.component.html',
  styleUrls: ['./ftm-activity-templates.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QueryParamService],
})
export class FtmActivityTemplatesComponent implements OnInit, OnDestroy, AfterViewInit {
  // TODO flex-act this will have to change
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  @ViewChild(PaginatorComponent)
  paginator: PaginatorComponent;

  /** General Observables */
  private _destroyedSubject = new ReplaySubject<void>(1);
  public readonly media$: Observable<any>;

  private _activeSortColumnSubject = new BehaviorSubject<string>(null);
  public readonly activeSortColumn$ = this._activeSortColumnSubject.asObservable();

  public dataSource = new MatTableDataSource();

  // this indicator is responsible for initial load only
  private _isLoading = new BehaviorSubject(true);
  public isLoading$ = this._isLoading.asObservable();

  public isFlexAdmin = this._flexPermissionService.isFlexTimeAdmin();

  public queryParams$ = this._queryParamService.useQueryParams(flexActivityTemplateQuerySchema.parse);

  public readonly displayedColumns$ = this._media.breakpoint$.pipe(
    takeUntil(this._destroyedSubject),
    map(breakpoint => {
      const columns = FLEX_ACTIVITY_TEMPLATE_TABLE_COLUMNS;

      const displayedColumns = ['xsmall', 'small'].includes(breakpoint) ? ['mobile'] : columns || [];
      return displayedColumns;
    }),
  );

  public MESSAGES = FtmActivityTemplatesMessages;

  constructor(
    public mediaObserver: MediaObserver,
    private _ftmActivityTemplatesService: FtmActivityTemplatesService,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _sentry: SentryService,
    private _authInfoService: AuthInfoService,
    private _flexPermissionService: FlexTimePermissionsService,
    private _queryParamService: QueryParamService<FlexActivityTemplateQueryParams>,
    private _media: MediaService,
    private _cdr: ChangeDetectorRef,
  ) {
    this.media$ = this.mediaObserver.asObservable().pipe(
      takeUntil(this._destroyedSubject),
      map(change => change[0].mqAlias),
      distinctUntilChanged(),
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator.matPaginator;
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string): string => {
      if (sortHeaderId === 'createdByPerson.firstName') {
        return data.createdByPerson.firstName;
      }
      if (sortHeaderId === 'createdByPerson.lastName') {
        return data.createdByPerson.lastName;
      }
      return data[sortHeaderId];
    };

    this.dataSource.sort.active = 'createdByPerson.lastName';
    this.dataSource.sort.direction = 'asc';
    this.dataSource.filterPredicate = (data: FlexTimeActivity, filter: string) => {
      let params: any = {};
      try {
        params = JSON.parse(filter);
      } catch {}
      const { teacher_hash, title } = params;
      const formattedTitle = title?.toLowerCase() || '';

      const filterTeacherHash = teacher_hash ? data.createdByPerson.hash === teacher_hash : true;
      const filterTitle = formattedTitle ? data.name.toLowerCase().includes(formattedTitle) : true;

      return [filterTeacherHash, filterTitle].every(Boolean);
    };
  }

  ngOnInit(): void {
    this._initialFetch();

    combineLatest([this._ftmActivityTemplatesService.activities$, this.queryParams$])
      .pipe(takeUntil(this._destroyedSubject))
      .subscribe(([activities, params]) => {
        this.dataSource.data = activities;

        // update filtering
        this.dataSource.filter = JSON.stringify(params);
        this._cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
    this._isLoading.complete();
  }

  get showEmptyState() {
    return this.dataSource.filteredData.length === 0;
  }

  public trackByFn(index, row: any): TrackByFunction<number> {
    return row.id || index;
  }

  public selectActiveColumn(columnKey: string) {
    this._activeSortColumnSubject.next(columnKey);
  }

  public async openEditModal(activity) {
    await this._ftmActivityTemplatesService.openEditModal(activity);
    await this._initialFetch(false);
  }

  public async copyItem(activity) {
    await this._ftmActivityTemplatesService.openEditModal(activity, true);
    await this._initialFetch(false);
  }

  public onTeacherSelectChange(teacherHash?: string) {
    this._queryParamService.updateQueryParams({
      teacher_hash: teacherHash || '',
    });
  }

  public setSearchTitle(title: string) {
    this._queryParamService.updateQueryParams({
      title,
    });
  }

  private async _initialFetch(showLoader = true) {
    try {
      this._isLoading.next(showLoader);
      const personHash = !this.isFlexAdmin ? this._authInfoService.authPersonHash : null;
      await this._ftmActivityTemplatesService.fetchAll(personHash);
    } catch (e) {
      this._systemAlertSnackBar.open({
        type: 'error',
        message: FtmActivityTemplatesMessages.SNACKBAR_FETCHING_ERROR,
      });
      this._sentry.captureMessageAsError(FtmActivityTemplatesMessages.SNACKBAR_FETCHING_ERROR, e);
    } finally {
      this._isLoading.next(false);
    }
  }
}
